/**
 * Progress Bar Plugin
 * Displays a fill bar showing carousel navigation progress
 *
 * Features:
 * - Fill bar style progress indicator
 * - Page 0 = 0% (empty), last page = 100% (full)
 * - Smooth animation based on actual track position
 * - Two-phase loop transitions: exit one side, re-enter from opposite side
 */

export default class ProgressBar {
	static pluginName = 'progress-bar';
	static defaultOptions = { progressBar: false };

	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.enabled = false;
		_.progressOptions = {};
		_.progressContainer = null;
		_.progressTrack = null;
		_.progressFill = null;
		_.isAutoGenerated = false;

		// animation state for loop transitions
		_.isInLoopTransition = false;
		_.loopDirection = 0; // 1 = forward, -1 = backward

		_.handlers = {
			trackPositionChanged: (payload) => _.handleTrackPositionChanged(payload),
			pageIndexChanged: () => _.handlePageIndexChanged(),
			optionsChanged: () => _.reInit(),
			layoutChanged: () => _.reInit(),
		};

		_.init();
	}

	/**
	 * Initialize the progress bar
	 */
	init() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		_.enabled = !!options.progressBar;

		// Always listen for option changes so it can be enabled later
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);

		if (!_.enabled) return;

		_.progressOptions = options.progressBar || {};
		_.queryDOM();
		_.bindEvents();
		_.render();
	}

	/**
	 * Reinitialize when options change
	 */
	reInit() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		const wasEnabled = _.enabled;
		_.enabled = !!options.progressBar;
		_.progressOptions = options.progressBar || {};

		if (_.enabled && !wasEnabled) {
			_.queryDOM();
			_.bindEvents();
			_.render();
		} else if (!_.enabled && wasEnabled) {
			_.unbindEvents();
			_.removeDOM();
		} else if (_.enabled) {
			_.render();
		}
	}

	/**
	 * Query and setup the progress bar container
	 */
	queryDOM() {
		const _ = this;

		// if a progress bar element is provided via selector, use it
		if (_.progressOptions.selector) {
			const element = document.querySelector(_.progressOptions.selector);
			if (element) _.progressContainer = element;
		}

		// if no container provided, generate one
		if (!_.progressContainer) {
			_.isAutoGenerated = true;
			_.progressContainer = document.createElement('div');
			_.ctx.carousel.appendChild(_.progressContainer);
		}

		// setup container
		_.progressContainer.classList.add('tarot-progress-container');
		_.progressContainer.setAttribute('role', 'progressbar');
		_.progressContainer.setAttribute('aria-valuemin', '0');
		_.progressContainer.setAttribute('aria-valuemax', '100');
		_.progressContainer.setAttribute('aria-valuenow', '0');
		_.progressContainer.setAttribute('aria-label', 'Carousel progress');

		// create track (background)
		_.progressTrack = document.createElement('div');
		_.progressTrack.classList.add('tarot-progress-track');
		_.progressContainer.appendChild(_.progressTrack);

		// create fill (foreground)
		_.progressFill = document.createElement('div');
		_.progressFill.classList.add('tarot-progress-fill');
		_.progressTrack.appendChild(_.progressFill);
	}

	/**
	 * Bind events
	 */
	bindEvents() {
		const _ = this;
		const { emitter, events } = _.ctx;

		emitter.on(events.track.positionChanged, _.handlers.trackPositionChanged);
		emitter.on(events.store.pageIndexChanged, _.handlers.pageIndexChanged);
		emitter.on(events.store.layoutChanged, _.handlers.layoutChanged);
	}

	/**
	 * Unbind events (except optionsChanged which is always needed)
	 */
	unbindEvents() {
		const _ = this;
		const { emitter, events } = _.ctx;

		emitter.off(events.track.positionChanged, _.handlers.trackPositionChanged);
		emitter.off(events.store.pageIndexChanged, _.handlers.pageIndexChanged);
		emitter.off(events.store.layoutChanged, _.handlers.layoutChanged);
	}

	/**
	 * Remove auto-generated DOM elements
	 */
	removeDOM() {
		const _ = this;
		if (_.isAutoGenerated && _.progressContainer && _.progressContainer.parentNode === _.ctx.carousel) {
			_.ctx.carousel.removeChild(_.progressContainer);
		}
		_.progressContainer = null;
		_.progressTrack = null;
		_.progressFill = null;
		_.isAutoGenerated = false;
	}

	/**
	 * Initial render
	 */
	render() {
		const _ = this;
		const state = _.ctx.store.getState();

		// hide if only one page
		if (state.pageCount <= 1) {
			_.hide();
			return;
		}

		// show based on options
		if (_.progressOptions.show !== false) {
			_.show();
		} else {
			_.hide();
		}

		// set initial progress
		_.updateProgress(_.calculatePageProgress());
	}

	/**
	 * Handle track position changes for smooth progress updates
	 * Implements two-phase loop transitions for seamless looping animation
	 */
	handleTrackPositionChanged(payload) {
		const _ = this;
		const { trackPercent } = payload;
		const state = _.ctx.store.getState();

		// hide if only one page
		if (state.pageCount <= 1) return;

		// detect loop transitions with two-phase animation
		if (state.canLoop && trackPercent > 1) {
			// forward loop transition (page N → page 0)
			if (!_.isInLoopTransition || _.loopDirection !== 1) {
				_.isInLoopTransition = true;
				_.loopDirection = 1;
			}
			const loopProgress = trackPercent - 1; // 0 → 1
			if (loopProgress < 0.5) {
				// phase 1: bar exits off right edge
				_.updateLoopExitRight(loopProgress * 2);
			} else {
				// phase 2: bar is empty at page 0
				_.updateProgress(0);
			}
		} else if (state.canLoop && trackPercent < 0) {
			// backward loop transition (page 0 → page N)
			if (!_.isInLoopTransition || _.loopDirection !== -1) {
				_.isInLoopTransition = true;
				_.loopDirection = -1;
			}
			const loopProgress = Math.abs(trackPercent); // 0 → 1
			if (loopProgress < 0.5) {
				// phase 1: bar is already empty at page 0
				_.updateProgress(0);
			} else {
				// phase 2: bar re-enters from right, settles at 100%
				_.updateLoopEnterRight((loopProgress - 0.5) * 2);
			}
		} else {
			// normal range (0-1 maps directly to 0%-100%)
			if (_.isInLoopTransition) {
				_.isInLoopTransition = false;
				_.loopDirection = 0;
				// reset fill to normal state
				_.progressFill.style.left = '';
				_.progressFill.style.right = '';
			}
			_.updateProgress(trackPercent);
		}
	}

	/**
	 * Handle page index changes (for discrete updates)
	 */
	handlePageIndexChanged() {
		const _ = this;

		// if not in a loop transition, update to page-based progress
		if (!_.isInLoopTransition) {
			_.updateProgress(_.calculatePageProgress());
		}
	}

	/**
	 * Calculate page-based progress (0 to 1)
	 * Returns pageIndex / (pageCount - 1) so page 0 = 0%, last page = 100%
	 */
	calculatePageProgress() {
		const state = this.ctx.store.getState();
		const { pageIndex, pageCount } = state;

		if (pageCount <= 1) return 1;
		return pageIndex / (pageCount - 1);
	}

	/**
	 * Update progress bar fill (normal state)
	 * @param {number} percent - Progress from 0 to 1
	 */
	updateProgress(percent) {
		const _ = this;
		if (!_.progressFill) return;

		const widthPercent = Math.max(0, Math.min(100, percent * 100));

		// normal fill: grows from left
		_.progressFill.style.width = `${widthPercent}%`;
		_.progressFill.style.left = '0';
		_.progressFill.style.right = '';

		// update ARIA
		_.progressContainer.setAttribute('aria-valuenow', Math.round(widthPercent));
	}

	/**
	 * Forward loop phase 1: Bar slides right and exits off right edge
	 * @param {number} progress - Progress through phase (0 to 1)
	 */
	updateLoopExitRight(progress) {
		const _ = this;
		if (!_.progressFill) return;

		const clamped = Math.max(0, Math.min(1, progress));
		// bar at 100% slides right, left edge moves from 0% → 100%
		const leftPercent = clamped * 100;
		_.progressFill.style.left = `${leftPercent}%`;
		_.progressFill.style.right = '0';
		_.progressFill.style.width = '';

		// ARIA: 100 → decreasing as bar exits
		const ariaValue = Math.round((1 - clamped) * 100);
		_.progressContainer.setAttribute('aria-valuenow', ariaValue);
	}

	/**
	 * Backward loop phase 2: Bar re-enters from right and settles at 100%
	 * @param {number} progress - Progress through phase (0 to 1)
	 */
	updateLoopEnterRight(progress) {
		const _ = this;
		if (!_.progressFill) return;

		const clamped = Math.max(0, Math.min(1, progress));
		// bar grows from right: 0% → 100%
		const rightPercent = (1 - clamped) * 100;
		_.progressFill.style.left = '';
		_.progressFill.style.right = `${rightPercent}%`;
		_.progressFill.style.width = `${clamped * 100}%`;

		// ARIA: 0 → increasing to 100
		_.progressContainer.setAttribute('aria-valuenow', Math.round(clamped * 100));
	}

	/**
	 * Show the progress bar
	 */
	show() {
		this.progressContainer.style.display = '';
	}

	/**
	 * Hide the progress bar
	 */
	hide() {
		this.progressContainer.style.display = 'none';
	}

	/**
	 * Destroy and cleanup
	 */
	destroy() {
		const _ = this;
		_.unbindEvents();
		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.removeDOM();
	}
}

// Queue the plugin (works regardless of whether Tarot is loaded)
(window.TarotPluginQueue = window.TarotPluginQueue || []).push({
	pluginName: 'progress-bar',
	plugin: ProgressBar
});
